import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type { Agent } from '../agent'
import type * as common from '../common'

import type { Access } from './access'
import type { App } from './app'
import type { Context } from './context'
import type { User } from './user'

// NOTE: this makes creating a TypeChecker easier
type ZoneBase = common.Identified<Zone.Id> &
  common.Coded &
  common.Named &
  common.Aliased &
  common.Described.Optionally &
  common.CreatedBy<User.Id>
/**
 * Represents a core zone for a {@link Tenant}. Every {@link App} in a
 * {@link Zone} has access to the same core data. This core data is managed
 * by and made available to the {@link App apps} through the DMS
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface Zone extends ZoneBase {}
export namespace Zone {
  /**
   * Identifies a {@link Zone}
   */
  export type Id = string
  export namespace Id {
    export const isId: TypeChecker<Id> =
      TypeChecker.isString.usingAlias('Zone.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Information needed to create a {@link Zone}
   */
  export type Input = Omit<Zone, 'createdBy' | 'id'>
  export namespace Input {
    export const DEFAULT: Readonly<Input> = {
      alias: 'main',
      code: 'Main',
      description:
        'This is the primary zone for all institution activity in Attain Apps. All data within here should be accurate and reliable',
      name: 'Main',
    }
  }

  /**
   * Information used to update a {@link Zone}
   */
  export type Updates = Partial<Input>

  /**
   * {@link Zone} with additional information pertaining to the associated
   * {@link User}
   */
  export interface ForUser extends Zone, Access.Holder {
    /**
     * {@link App.ForUser} for the apps that this user has access to in
     * this {@link Zone.ForUser}
     */
    apps: readonly App.ForUser[]
    /**
     * {@link Agent.ForUser} for the agents this user has access to at the
     * zone level
     */
    agents: readonly Agent.ForUser[]
  }

  /**
   * {@link Zone} that also has the full {@link Context.ForZone}
   */
  export type InContext = Zone & Context.ForZone
}
